import { Component, OnInit, OnDestroy, Input, ViewChild, Output, EventEmitter, ElementRef, AfterViewInit, Inject } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { Observable, ReplaySubject, takeUntil } from 'rxjs';
import { FINGERPRINT_QUALITY_LIMIT_FOR_ENROLL } from 'types';
import { AppContext } from '../../base/control/context';
import { FingerprintsBlob } from '../../base/model/features';
import { ValidationService } from '../../base/validation/validation.service';
import { HWServiceConfig } from '../../service/hw-service-config';
import { FpScannerInfo } from '../../service/hw-service-model';
import { PassportReaderService } from '../../service/passport-reader.service';
import { FingerPrintsService, FingerPrintsServiceFactory, FingerPrintServiceResponse } from '../../service/finger-prints.service';
import { SvgDragService } from '../../base/directive/svg-drag.service';
import { SvgDragEndedEvent } from '../../base/directive/svg-parent-drag.directive';

enum FinalState {
  NOT_SCANNED,
  DAMAGED,
  AMPUTATED,
  ENCRYPTED,
}

enum State {
  NOT_SCANNED,
  SELECTED,
  SCANNING,
  SCANNED,
  DAMAGED,
  AMPUTATED,
  ENCRYPTED,
}

enum Fingers {
  right_little,
  right_ring,
  right_middle,
  right_index,
  right_thumb,
  left_thumb,
  left_index,
  left_middle,
  left_ring,
  left_little,
}

@Component({
  selector: 'i-fingerprint',
  templateUrl: './fingerprint.component.html',
  styleUrls: ['./fingerprint.component.scss'],
  providers: [
    { provide: PassportReaderService, useClass: PassportReaderService },
    { provide: FingerPrintsServiceFactory, useValue: { create: (c: HWServiceConfig) => new FingerPrintsService(c) } },
    { provide: SvgDragService, useClass: SvgDragService },
  ],
})
export class FingerprintComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() readonly: boolean = false;
  @Input() flags: FingerprintsBlob;
  @Output() flagsChange = new EventEmitter<void>();
  @Output() blobChange = new EventEmitter<string>();

  @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger;

  @ViewChild('target0') target0: ElementRef<SVGPathElement>;
  @ViewChild('target1') target1: ElementRef<SVGPathElement>;
  @ViewChild('target2') target2: ElementRef<SVGPathElement>;
  @ViewChild('target3') target3: ElementRef<SVGPathElement>;
  @ViewChild('target4') target4: ElementRef<SVGPathElement>;
  @ViewChild('target5') target5: ElementRef<SVGPathElement>;
  @ViewChild('target6') target6: ElementRef<SVGPathElement>;
  @ViewChild('target7') target7: ElementRef<SVGPathElement>;
  @ViewChild('target8') target8: ElementRef<SVGPathElement>;
  @ViewChild('target9') target9: ElementRef<SVGPathElement>;

  contextMenuPosition = { x: '0px', y: '0px' };
  FINGERS_STATE: FINGER_STATE[] = [];
  process: boolean = false;
  notificationEncrypt: string = '';
  private disableScaningAtStart: boolean = true;
  private scanners: FpScannerInfo[] = [];
  private dead = new ReplaySubject<void>();
  private scanningFingers: FINGER_STATE[];

  private getFingerState(id: string): FinalState {
    return this.flags.items[(Fingers as any)[id]].scanned;
  }

  private setFingerState(id: string, state: FinalState) {
    this.flags.items[(Fingers as any)[id]].scanned = state;
    this.validationService.validate();
    this.flagsChange.emit();
  }

  constructor(
    private validationService: ValidationService,
    private passportReaderService: PassportReaderService,
    private appContext: AppContext,
    private config: HWServiceConfig,
    private fingerPrintsServiceFactory: FingerPrintsServiceFactory,
    private dragService: SvgDragService,
    @Inject(FINGERPRINT_QUALITY_LIMIT_FOR_ENROLL) public qualityLimit: number,
  ) {}

  private fingerPrintService: FingerPrintsService;

  reset() {
    this.process = false;
    this.disableScaningAtStart = true;
    this.FINGERS_STATE.forEach((x) => {
      if (x.state === State.SCANNING) {
        x.state = State.SELECTED;
      }
    });

    if (this.fingerPrintService) {
      this.fingerPrintService.disconnect();
    }

    this.dead.next();
    this.dead.complete();
    this.dead = new ReplaySubject();
    this.fingerPrintService = this.fingerPrintsServiceFactory.create(this.config);
    this.fingerPrintService.connect();
    setTimeout(() => {
      this.fingerPrintService
        .GetScannersList()
        .pipe(takeUntil(this.dead))
        .subscribe(
          (r) => {
            this.scanners = r;
            this.disableScaningAtStart = false;
          },
          (e) => {
            this.appContext.getMessageSink()('ERROR', 'fingerprints.error.connection');
          },
        );
    }, 1000);
  }

  ngOnInit() {
    for (let i = 0; i < 10; i++) {
      this.FINGERS_STATE[i] = new FINGER_STATE();
      this.FINGERS_STATE[i].name = Fingers[i];
      switch (this.getFingerState((Fingers as any)[i])) {
        case FinalState.NOT_SCANNED:
          this.FINGERS_STATE[i].state = State.NOT_SCANNED;
          break;
        case FinalState.DAMAGED:
          this.FINGERS_STATE[i].state = State.DAMAGED;
          break;
        case FinalState.AMPUTATED:
          this.FINGERS_STATE[i].state = State.AMPUTATED;
          break;
        case FinalState.ENCRYPTED:
          this.FINGERS_STATE[i].state = State.ENCRYPTED;
          break;
      }
    }

    if (this.canSelect() === 2) {
      this.startIndexFingersSelection();
    }

    if (!this.readonly) {
      this.passportReaderService.connect();
      this.reset();
    }
  }

  ngAfterViewInit() {
    this.dragService.registerTarget(this.target0.nativeElement, 0);
    this.dragService.registerTarget(this.target1.nativeElement, 1);
    this.dragService.registerTarget(this.target2.nativeElement, 2);
    this.dragService.registerTarget(this.target3.nativeElement, 3);
    this.dragService.registerTarget(this.target4.nativeElement, 4);
    this.dragService.registerTarget(this.target5.nativeElement, 5);
    this.dragService.registerTarget(this.target6.nativeElement, 6);
    this.dragService.registerTarget(this.target7.nativeElement, 7);
    this.dragService.registerTarget(this.target8.nativeElement, 8);
    this.dragService.registerTarget(this.target9.nativeElement, 9);
  }

  private startIndexFingersSelection() {
    if (
      this.FINGERS_STATE[3].state === State.DAMAGED ||
      this.FINGERS_STATE[3].state === State.AMPUTATED ||
      this.FINGERS_STATE[6].state === State.DAMAGED ||
      this.FINGERS_STATE[6].state === State.AMPUTATED
    ) {
    } else {
      this.FINGERS_STATE[3].state = State.SELECTED;
      this.FINGERS_STATE[6].state = State.SELECTED;
    }
  }

  ngOnDestroy(): void {
    if (!this.readonly) {
      this.fingerPrintService.disconnect();
      this.passportReaderService.disconnect();
    }
    this.dead.next();
    this.dead.complete();
  }

  selectFinger(event: MouseEvent): void {
    event.preventDefault();
    const elt: SVGElement = event.currentTarget as SVGElement;
    let finger = this.FINGERS_STATE[Number(elt.id)];
    if (finger.state === State.SELECTED) {
      finger.state = State.NOT_SCANNED;
    } else if (finger.state === State.NOT_SCANNED && this.canSelect() > 0) {
      finger.state = State.SELECTED;
    }
  }

  onDrop(event: SvgDragEndedEvent) {
    if (this.FINGERS_STATE[event.index].state === State.SELECTED && this.FINGERS_STATE[event.targetIndex].state === State.NOT_SCANNED) {
      this.FINGERS_STATE[event.index].state = State.NOT_SCANNED;
      this.FINGERS_STATE[event.targetIndex].state = State.SELECTED;
    }
  }

  altChoise(event: MouseEvent): void {
    event.preventDefault();
    const elt = event.currentTarget as SVGElement;
    if (elt) {
      this.contextMenuPosition.x = `${event.clientX}px`;
      this.contextMenuPosition.y = `${event.clientY}px`;
      this.trigger.menuData = { item: Number(elt.id) };
      this.trigger._openedBy = 'mouse';
      this.trigger.openMenu();
    }
  }

  private canSelect() {
    return (
      2 -
      this.FINGERS_STATE.filter(
        (id) => id.state === State.SELECTED || id.state === State.SCANNING || id.state === State.SCANNED || id.state === State.ENCRYPTED,
      ).length
    );
  }

  get disabledStartNew(): boolean {
    return this.readonly || !this.FINGERS_STATE.filter((id) => id.state === State.ENCRYPTED).length;
  }

  get disabledScanning(): boolean {
    return (
      this.readonly || this.disableScaningAtStart || this.process || !this.FINGERS_STATE.filter((id) => id.state === State.SELECTED).length
    );
  }

  get disabledEncrypting(): boolean {
    return (
      this.readonly ||
      this.disableScaningAtStart ||
      this.process ||
      !this.FINGERS_STATE.filter((id) => id.state === State.SCANNED).length ||
      !!this.FINGERS_STATE.filter((id) => id.state === State.SELECTED).length
    );
  }

  damaged(item: number): void {
    if (this.FINGERS_STATE[item].state === State.ENCRYPTED) {
      this.cancelingEncrypt();
    }
    if (this.FINGERS_STATE[item].state !== State.SCANNING) {
      this.FINGERS_STATE[item].state = State.DAMAGED;
      this.setFingerState((Fingers as any)[item], FinalState.DAMAGED);
    }
  }

  amputated(item: number): void {
    if (this.FINGERS_STATE[item].state === State.ENCRYPTED) {
      this.cancelingEncrypt();
    }
    if (this.FINGERS_STATE[item].state !== State.SCANNING) {
      this.FINGERS_STATE[item].state = State.AMPUTATED;
      this.setFingerState((Fingers as any)[item], FinalState.AMPUTATED);
    }
  }

  clear(item: number): void {
    if (this.FINGERS_STATE[item].state === State.ENCRYPTED) {
      this.cancelingEncrypt();
    }
    if (this.FINGERS_STATE[item].state !== State.SCANNING) {
      this.FINGERS_STATE[item].state = State.NOT_SCANNED;
      this.setFingerState((Fingers as any)[item], FinalState.NOT_SCANNED);
    }
  }

  startNew(): void {
    this.cancelingEncrypt();
    this.startIndexFingersSelection();
  }

  scan(): void {
    this.cancelingEncrypt();
    this.process = true;
    this.FINGERS_STATE.forEach((x) => {
      if (x.state === State.SELECTED) {
        x.state = State.SCANNING;
      }
    });
    this.scanningFingers = this.FINGERS_STATE.filter((id) => id.state === State.SCANNING);
    let ob: Observable<any>;
    const scaner1 = this.scanners.find((device) => device.tag === 'left')!.id!;
    const scaner2 = this.scanners.find((device) => device.tag === 'right')!.id!;
    if (this.scanningFingers.length === 1) {
      let scaner: number;
      if (this.FINGERS_STATE.findIndex((id) => id.state === State.SCANNING) < 5) {
        scaner = scaner1;
      } else {
        scaner = scaner2;
      }
      ob = this.fingerPrintService.ScanFinger1(scaner, this.qualityLimit);
    } else {
      ob = this.fingerPrintService.ScanFinger2(scaner1, scaner2, this.qualityLimit);
    }
    ob.pipe(takeUntil(this.dead)).subscribe(
      (r) => {
        if (Array.isArray(r)) {
          if (this.scanningFingers.length === 1) {
            this.scanningFingers[0].state = State.SCANNED;
          } else {
            r.forEach((id) => {
              if (id === scaner1) {
                this.scanningFingers[0].state = State.SCANNED;
              }
              if (id === scaner2) {
                this.scanningFingers[1].state = State.SCANNED;
              }
            });
          }
        } else if (r === 'stop_bad_quality!') {
          this.appContext.getMessageSink()('ERROR', 'fingerprints.error.badquality');
          this.scanningFingers.forEach((x) => {
            x.state = State.SELECTED;
          });
        } else if (r instanceof FingerPrintServiceResponse) {
          if (this.scanningFingers.length === 1) {
            this.scanningFingers[0].data = r.image;
            this.appContext.getMessageSink()('INFO', 'fingerprints.success.goodquality');
          } else {
            if (r.scannerId === scaner1) {
              if (r.image) {
                this.scanningFingers[0].data = r.image;
              } else {
                this.scanningFingers[0].state = State.SELECTED;
              }
            }
            if (r.scannerId === scaner2) {
              if (r.image) {
                this.scanningFingers[1].data = r.image;
              } else {
                this.scanningFingers[1].state = State.SELECTED;
              }
            }
            if (r.image) {
              this.appContext.getMessageSink()('INFO', 'fingerprints.success.goodquality');
            } else {
              this.appContext.getMessageSink()('WARN', 'fingerprints.error.badquality');
            }
          }
        }
      },
      (e) => {
        this.appContext.getMessageSink()('ERROR', 'fingerprints.error.scaning');
        this.scanningFingers.forEach((x) => {
          x.state = State.SELECTED;
        });
      },
      () => {
        this.process = false;
      },
    );
  }

  encrypt() {
    this.process = true;
    this.notificationEncrypt = this.appContext.getI18NString('fingerprints.process.encrypt');
    const EncryptFingers: any[] = [];
    this.FINGERS_STATE.forEach((id) => {
      if (id.state === State.SCANNED) {
        EncryptFingers.push([id.name.split('_').join(' '), id.data]);
      }
    });
    this.passportReaderService
      .EncryptFingers(EncryptFingers)
      .pipe(takeUntil(this.dead))
      .subscribe(
        (r) => {
          this.appContext.getMessageSink()('INFO', 'fingerprints.success.encrypt');
          this.process = false;
          this.notificationEncrypt = '';
          this.FINGERS_STATE.filter((id) => id.state === State.SCANNED).forEach((element) => {
            element.state = State.ENCRYPTED;
            element.data = '';
            this.setFingerState((Fingers as any)[this.FINGERS_STATE.findIndex((id) => id.name === element.name)], FinalState.ENCRYPTED);
          });
          this.flags.hash = r.hash;
          this.flags.cert = r.cert;
          this.flags.ekey = r.ekey;
          this.flags.digestURN = r.digestURN;
          this.flags.transportURN = r.transportURN;
          this.blobChange.emit(r.data);
        },
        (e) => {
          this.appContext.getMessageSink()('ERROR', 'fingerprints.error.encrypt');
          this.process = false;
          this.notificationEncrypt = e;
        },
      );
  }

  private cancelingEncrypt() {
    this.flags.hash = '';
    this.flags.cert = '';
    this.flags.ekey = '';
    this.flags.digestURN = '';
    this.flags.transportURN = '';
    this.blobChange.emit('');
    this.FINGERS_STATE.filter((id) => id.state === State.ENCRYPTED).forEach((element) => {
      element.state = State.NOT_SCANNED;
      this.setFingerState((Fingers as any)[this.FINGERS_STATE.findIndex((id) => id.name === element.name)], FinalState.NOT_SCANNED);
    });
  }
}

export class FINGER_STATE {
  name: string;
  state: State;
  data: string;

  get mark(): boolean {
    return this.state === State.SELECTED || this.state === State.SCANNING;
  }

  get animate(): boolean {
    return this.state === State.SCANNING;
  }
}
